import * as React from 'react'
import {
    Container,
    Grid2,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
    Autocomplete,
    Chip,
    Divider,
    MenuItem,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import Navbar from '../setup/Navbar'
import HeadingText from '../../BuiltInComponent/HeadingTest'
import ButtonNormal from '../../BuiltInComponent/ButtonNormal'
import Loading from '../../BuiltInComponent/Loading'
import { useDispatch, useSelector } from 'react-redux'
import {
    getItemsWithStock,
    getIssuances,
    getDepartments,
    saveManualIssuance,
    resetProductionState
} from '../../features/AdminManagement/productionSlice'

const todayDate = () => new Date().toISOString().slice(0, 10)

const EMPTY_ROW = { itemCode: '', itemDesc: '', quantity: '', inUnit: '', currentStock: 0 }

export default function ManualIssuance() {
    const dispatch = useDispatch()
    const { itemsWithStock, issuances, departments, isLoading, isSuccess, isError, message } = useSelector(s => s.production)
    const { user } = useSelector(s => s.auth)

    const [branchID, setBranchID] = React.useState(1)
    const [departmentID, setDepartmentID] = React.useState('')
    const [remarks, setRemarks] = React.useState('')
    const [rows, setRows] = React.useState([{ ...EMPTY_ROW }])

    // History filters
    const [hFilter, setHFilter] = React.useState({
        branchID: 1,
        departmentID: '',
        fromDate: todayDate(),
        toDate: todayDate(),
        itemCode: ''
    })

    React.useEffect(() => {
        dispatch(getItemsWithStock({ branchID }))
        dispatch(getIssuances({ branchID: hFilter.branchID, fromDate: hFilter.fromDate, toDate: hFilter.toDate }))
        dispatch(getDepartments())
        // eslint-disable-next-line
    }, [dispatch])

    React.useEffect(() => {
        if (isSuccess) {
            alert('Issuance saved successfully!')
            setRows([{ ...EMPTY_ROW }])
            setRemarks('')
            dispatch(getItemsWithStock({ branchID }))
            dispatch(getIssuances({ branchID: hFilter.branchID, fromDate: hFilter.fromDate, toDate: hFilter.toDate }))
            dispatch(resetProductionState())
        }
        if (isError) {
            alert('Error: ' + message)
            dispatch(resetProductionState())
        }
        // eslint-disable-next-line
    }, [isSuccess, isError])

    const onBranchChange = (val) => {
        setBranchID(val)
        dispatch(getItemsWithStock({ branchID: val }))
    }

    // ---- item rows ----
    const onItemSelect = (index, selectedItem) => {
        if (!selectedItem) {
            const updated = [...rows]
            updated[index] = { ...EMPTY_ROW }
            setRows(updated)
            return
        }
        const updated = [...rows]
        updated[index] = {
            itemCode: selectedItem.itemCode,
            itemDesc: selectedItem.itemDesc,
            inUnit: selectedItem.inUnit,
            currentStock: selectedItem.currentStock,
            quantity: ''
        }
        setRows(updated)
    }

    const onQtyChange = (index, val) => {
        const updated = [...rows]
        updated[index] = { ...updated[index], quantity: val }
        setRows(updated)
    }

    const addRow = () => setRows(prev => [...prev, { ...EMPTY_ROW }])

    const removeRow = (index) => {
        if (rows.length === 1) { setRows([{ ...EMPTY_ROW }]); return }
        setRows(prev => prev.filter((_, i) => i !== index))
    }

    // ---- Save ----
    const onSave = () => {
        const validItems = rows.filter(r => r.itemCode && Number(r.quantity) > 0)
        if (validItems.length === 0) {
            alert('Kam az kam ek item aur quantity add karen')
            return
        }
        dispatch(saveManualIssuance({
            branchID,
            departmentID: departmentID || null,
            remarks,
            issuedBy: user?.userID || null,
            items: validItems.map(r => ({ itemCode: r.itemCode, quantity: Number(r.quantity) }))
        }))
    }

    // ---- History search ----
    const onHistorySearch = () => {
        dispatch(getIssuances({
            branchID: hFilter.branchID || undefined,
            departmentID: hFilter.departmentID || undefined,
            fromDate: hFilter.fromDate || undefined,
            toDate: hFilter.toDate || undefined,
            itemCode: hFilter.itemCode || undefined
        }))
    }

    const formatDate = (dt) => dt ? new Date(dt).toLocaleString('en-PK') : ''

    return (
        <>
            <Navbar />
            <Container maxWidth="xl">
                <Grid2 container spacing={2} sx={{ mt: 2, mb: 4 }}>

                    {/* Heading */}
                    <Grid2 size={12}>
                        <HeadingText text="Manual Inventory Issuance" />
                    </Grid2>

                    {/* ---- Top controls ---- */}
                    <Grid2 size={3}>
                        <TextField
                            select fullWidth size="small" label="Branch"
                            value={branchID}
                            onChange={e => onBranchChange(Number(e.target.value))}
                        >
                            <MenuItem value={1}>Main Branch</MenuItem>
                            <MenuItem value={2}>Shah Faisal No.1</MenuItem>
                        </TextField>
                    </Grid2>
                    <Grid2 size={3}>
                        <TextField
                            select fullWidth size="small" label="Department"
                            value={departmentID}
                            onChange={e => setDepartmentID(e.target.value)}
                        >
                            <MenuItem value="">-- Select Department --</MenuItem>
                            {departments.map(d => (
                                <MenuItem key={d.departmentID} value={d.departmentID}>{d.departmentName}</MenuItem>
                            ))}
                        </TextField>
                    </Grid2>
                    <Grid2 size={6}>
                        <TextField
                            fullWidth size="small" label="Remarks (Optional)"
                            value={remarks}
                            onChange={e => setRemarks(e.target.value)}
                        />
                    </Grid2>

                    {/* ---- Items Table ---- */}
                    <Grid2 size={12}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                Issue Items
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#780202' }}>
                                            <TableCell sx={{ color: '#fff', width: '40%' }}>Item</TableCell>
                                            <TableCell sx={{ color: '#fff', width: '15%' }} align="center">Current Stock</TableCell>
                                            <TableCell sx={{ color: '#fff', width: '10%' }} align="center">Unit</TableCell>
                                            <TableCell sx={{ color: '#fff', width: '20%' }} align="center">Issue Quantity</TableCell>
                                            <TableCell sx={{ color: '#fff', width: '15%' }} align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>
                                                    <Autocomplete
                                                        size="small"
                                                        options={itemsWithStock}
                                                        getOptionLabel={o => `${o.itemDesc}`}
                                                        value={itemsWithStock.find(i => i.itemCode === row.itemCode) || null}
                                                        onChange={(_, val) => onItemSelect(idx, val)}
                                                        renderInput={params => <TextField {...params} placeholder="Item search..." />}
                                                        isOptionEqualToValue={(o, v) => o.itemCode === v.itemCode}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={Number(row.currentStock).toFixed(3)}
                                                        size="small"
                                                        color={row.currentStock > 0 ? 'success' : 'error'}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2">{row.inUnit}</Typography>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <TextField
                                                        size="small" type="number"
                                                        inputProps={{ min: 0, step: 0.001 }}
                                                        value={row.quantity}
                                                        onChange={e => onQtyChange(idx, e.target.value)}
                                                        sx={{ width: 120 }}
                                                    />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <IconButton size="small" color="error" onClick={() => removeRow(idx)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Grid2 container spacing={2} sx={{ mt: 1 }}>
                                <Grid2 size={6}>
                                    <ButtonNormal
                                        text="+ Add Row"
                                        startIcon={<AddCircleOutlineIcon />}
                                        onClick={addRow}
                                        variant="outlined"
                                    />
                                </Grid2>
                                <Grid2 size={6} sx={{ textAlign: 'right' }}>
                                    {isLoading ? <Loading /> :
                                        <ButtonNormal text="Save Issuance" onClick={onSave} />
                                    }
                                </Grid2>
                            </Grid2>
                        </Paper>
                    </Grid2>

                    {/* ---- Divider ---- */}
                    <Grid2 size={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6" fontWeight="bold">Issuance History</Typography>
                    </Grid2>

                    {/* ---- History Filters ---- */}
                    <Grid2 size={2}>
                        <TextField
                            select fullWidth size="small" label="Department"
                            value={hFilter.departmentID}
                            onChange={e => setHFilter(p => ({ ...p, departmentID: e.target.value }))}
                        >
                            <MenuItem value="">All Departments</MenuItem>
                            {departments.map(d => (
                                <MenuItem key={d.departmentID} value={d.departmentID}>{d.departmentName}</MenuItem>
                            ))}
                        </TextField>
                    </Grid2>
                    <Grid2 size={2}>
                        <TextField
                            select fullWidth size="small" label="Branch"
                            value={hFilter.branchID}
                            onChange={e => setHFilter(p => ({ ...p, branchID: Number(e.target.value) }))}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value={1}>Main Branch</MenuItem>
                            <MenuItem value={2}>Shah Faisal No.1</MenuItem>
                        </TextField>
                    </Grid2>
                    <Grid2 size={2}>
                        <TextField
                            fullWidth size="small" type="date" label="From Date"
                            InputLabelProps={{ shrink: true }}
                            value={hFilter.fromDate}
                            onChange={e => setHFilter(p => ({ ...p, fromDate: e.target.value }))}
                        />
                    </Grid2>
                    <Grid2 size={2}>
                        <TextField
                            fullWidth size="small" type="date" label="To Date"
                            InputLabelProps={{ shrink: true }}
                            value={hFilter.toDate}
                            onChange={e => setHFilter(p => ({ ...p, toDate: e.target.value }))}
                        />
                    </Grid2>
                    <Grid2 size={3}>
                        <Autocomplete
                            size="small"
                            options={itemsWithStock}
                            getOptionLabel={o => o.itemDesc}
                            value={itemsWithStock.find(i => i.itemCode === hFilter.itemCode) || null}
                            onChange={(_, val) => setHFilter(p => ({ ...p, itemCode: val ? val.itemCode : '' }))}
                            renderInput={params => <TextField {...params} label="Item (Optional)" />}
                            isOptionEqualToValue={(o, v) => o.itemCode === v.itemCode}
                        />
                    </Grid2>
                    <Grid2 size={2}>
                        <ButtonNormal text="Search" onClick={onHistorySearch} />
                    </Grid2>
                    <Grid2 size={1} />

                    {/* ---- History Table ---- */}
                    <Grid2 size={12}>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell><b>#</b></TableCell>
                                        <TableCell><b>Date / Time</b></TableCell>
                                        <TableCell><b>Item</b></TableCell>
                                        <TableCell align="right"><b>Qty Issued</b></TableCell>
                                        <TableCell align="right"><b>Balance After</b></TableCell>
                                        <TableCell><b>Branch</b></TableCell>
                                        <TableCell><b>Department</b></TableCell>
                                        <TableCell><b>Issued By</b></TableCell>
                                        <TableCell><b>Remarks</b></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {issuances.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center">
                                                <Typography variant="body2" color="textSecondary">
                                                    No records found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : issuances.map((r, i) => (
                                        <TableRow key={r.ledgerID} hover>
                                            <TableCell>{i + 1}</TableCell>
                                            <TableCell>{formatDate(r.recordedAt)}</TableCell>
                                            <TableCell>{r.itemDesc}</TableCell>
                                            <TableCell align="right">{Number(r.quantity).toFixed(3)} {r.inUnit}</TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    label={Number(r.balanceAfter).toFixed(3)}
                                                    size="small"
                                                    color={r.balanceAfter >= 0 ? 'success' : 'warning'}
                                                />
                                            </TableCell>
                                            <TableCell>{r.branchName}</TableCell>
                                            <TableCell>{r.departmentName}</TableCell>
                                            <TableCell>{r.issuedBy}</TableCell>
                                            <TableCell>{r.remarks}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid2>

                </Grid2>
            </Container>
        </>
    )
}
